import {
  BriefcaseIcon,
  CalendarIcon,
  CreditCardIcon,
  HeartIcon,
  IdentificationIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

import { type EvidenceData } from '@/components/citations';
import {
  AddressDisplay,
  buildFullName,
  ContactHandleList,
  EntityCard,
  EntityCardHeader,
  EntityCardSection,
  EntityDisclosure,
  formatDate,
  formatEnum,
  toTitleCase,
} from '@/components/shared';
import { FieldWithApproval } from '@/components/suggestions';
import type {
  ReadPerson,
  ReadPersonWithRelationships,
} from '@/services/api/partiesService';

interface PersonCardProps {
  person: ReadPerson | ReadPersonWithRelationships;
  showRelationships?: boolean;
  onEdit?: () => void;
  onApproveField?: (fieldKey: string) => void;
  showApproval?: boolean;
  evidence?: EvidenceData[];
  evidenceTitle?: string;
  matterId?: string;
}

export const PersonCard = ({
  person,
  showRelationships = false,
  onEdit,
  onApproveField,
  showApproval = false,
  evidence,
  evidenceTitle,
  matterId,
}: PersonCardProps) => {
  const fullName = buildFullName(
    person.first_name,
    person.middle_name,
    person.last_name,
    person.suffix,
  );
  const formattedDOB = formatDate(person.date_of_birth);

  const hasRelationships =
    showRelationships &&
    'personal_relationships' in person &&
    ((person.personal_relationships?.length ?? 0) > 0 ||
      (person.employments?.length ?? 0) > 0);

  const hasContactInfo = (person.contact_handles?.length ?? 0) > 0;
  const hasAddress = person.address_line1 || person.city;
  const hasPersonalInfo =
    person.date_of_birth ||
    person.ssn ||
    person.drivers_license_number ||
    person.preferred_language;
  const hasAdditionalDetails = hasContactInfo || hasAddress || hasPersonalInfo;

  const badges = [
    person.is_minor && (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase text-amber-800">
        Minor
      </span>
    ),
    person.identity_status && person.identity_status !== 'named' && (
      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 capitalize">
        {person.identity_status}
      </span>
    ),
  ].filter(Boolean);

  return (
    <EntityCard
      icon={<UserIcon />}
      isClaimant={person.is_claimant}
      approvalMetadata={person.approval_metadata}
      showApproval={showApproval}
      onEdit={onEdit}
      evidence={evidence}
      evidenceTitle={evidenceTitle}
      matterId={matterId}
    >
      <EntityCardHeader
        title={fullName || person.short_description || 'Unnamed'}
        isClaimant={person.is_claimant}
        badges={badges}
      />

      {person.involvement_description && (
        <p className="mt-1 text-sm text-gray-600">
          {person.involvement_description}
        </p>
      )}

      {(person.stated_age || person.gender) && !showApproval && (
        <div className="mt-1 text-sm text-gray-500">
          {[
            person.stated_age && `${person.stated_age} years old`,
            person.gender && toTitleCase(person.gender),
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}

      {/* Compact contact info */}
      {!showApproval && person.contact_handles && (
        <EntityCardSection className="mt-2">
          <ContactHandleList handles={person.contact_handles} compact />
        </EntityCardSection>
      )}

      {/* Relationships */}
      {hasRelationships && (
        <EntityCardSection className="mt-2 space-y-1.5">
          {(person as ReadPersonWithRelationships).personal_relationships
            ?.slice(0, 2)
            .map((rel) => {
              const relatedName = buildFullName(
                rel.related_person.first_name,
                undefined,
                rel.related_person.last_name,
              );
              return (
                <div key={rel.id} className="flex items-center gap-1.5 text-sm">
                  <HeartIcon className="h-4 w-4 text-pink-500" />
                  <div>
                    <span className="text-xs text-gray-500 capitalize">
                      {formatEnum(rel.relationship_type)} ·{' '}
                    </span>
                    <span className="text-gray-900 font-medium">
                      {relatedName || rel.related_person.short_description}
                    </span>
                  </div>
                </div>
              );
            })}
          {(person as ReadPersonWithRelationships).employments?.map((emp) => (
            <div key={emp.id} className="flex items-center gap-1.5 text-sm">
              <BriefcaseIcon className="h-4 w-4 text-gray-400" />
              <div>
                <span className="text-xs text-gray-500">
                  Employer
                  {emp.acting_in_role_during_incident && ' (on duty)'} ·{' '}
                </span>
                <span className="text-gray-900 font-medium">
                  {emp.employer.legal_name ||
                    emp.employer.dba_name ||
                    emp.employer.short_description}
                </span>
              </div>
            </div>
          ))}
        </EntityCardSection>
      )}

      {/* Details disclosure */}
      {hasAdditionalDetails && (
        <EntityDisclosure>
          {showApproval && onApproveField ? (
            <dl className="space-y-2">
              <FieldWithApproval
                label="Date of Birth"
                value={formattedDOB}
                fieldKey="date_of_birth"
                approvalMetadata={person.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Age"
                value={person.stated_age ? `${person.stated_age} years` : null}
                fieldKey="stated_age"
                approvalMetadata={person.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="SSN"
                value={person.ssn}
                fieldKey="ssn"
                approvalMetadata={person.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Driver's License"
                value={
                  person.drivers_license_number
                    ? `${person.drivers_license_number}${
                        person.drivers_license_state_code
                          ? ` · ${person.drivers_license_state_code}`
                          : ''
                      }`
                    : null
                }
                fieldKey="drivers_license_number"
                approvalMetadata={person.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
            </dl>
          ) : (
            hasPersonalInfo && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Personal Info
                </div>
                <div className="space-y-1.5">
                  {formattedDOB && (
                    <div className="flex items-center gap-2 text-sm">
                      <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">DOB</div>
                        <div className="text-gray-900">{formattedDOB}</div>
                      </div>
                    </div>
                  )}
                  {person.ssn && (
                    <div className="flex items-center gap-2 text-sm">
                      <CreditCardIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">SSN</div>
                        <div className="text-gray-900">{person.ssn}</div>
                      </div>
                    </div>
                  )}
                  {person.drivers_license_number && (
                    <div className="flex items-center gap-2 text-sm">
                      <IdentificationIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-gray-500">
                          Driver&apos;s License
                        </div>
                        <div className="text-gray-900">
                          {person.drivers_license_number}
                          {person.drivers_license_state_code &&
                            ` · ${person.drivers_license_state_code}`}
                        </div>
                      </div>
                    </div>
                  )}
                  {person.preferred_language && (
                    <div className="text-sm text-gray-700">
                      Speaks {person.preferred_language}
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {hasContactInfo && person.contact_handles && (
            <ContactHandleList handles={person.contact_handles} />
          )}

          {hasAddress && (
            <AddressDisplay
              line1={person.address_line1}
              line2={person.address_line2}
              city={person.city}
              state={person.state}
              zip={person.zip}
            />
          )}
        </EntityDisclosure>
      )}
    </EntityCard>
  );
};
